import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { format } from 'date-fns';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useRecoilState } from 'recoil';

import {
  appendCorrelationToken,
  findItem,
  getConflictingEvents,
  notificationsToHashTable,
  planningEntitiesToEvents,
  sortByDate,
  sourceViewportContext,
  unassignAppointment as unassignAppointmentUtil
} from '../utils';
import showError from 'components/shared/showError';

import { mutations } from 'RestAPIs/rapini';

import { apDebug } from '../apDebug';
import {
  dialogManagerActiveAtom,
  planResponsePlanningEntityIdentifiersAtom,
  shouldShowPopoverDialogAtom
} from '../atoms';
import {
  clearDropVerification,
  recordDropForVerification
} from '../dropVerification';
import { setFrozenCardDrop } from '../frozenDrop.store';
import { isAnyDragActiveNow } from '../hooks/useIsAnyDragActive';

import { Box } from '@mui/material';
import { LoadingOverlay } from 'components/shared';
import TaskList, { TASK_LIST_WIDTH } from 'components/shared/TaskList/TaskList';
import { DialogType } from '../components/DialogManager/types';
import { PopOverConfirmationContext } from '../PopOverConfirmationContext';
import DragPortalPragmatic from './DragPortalPragmatic/DragPortalPragmatic';
import FrozenDropCardPragmatic from './FrozenDropCardPragmatic';
import RouteOverviewPragmatic from './RouteOverviewPragmatic/RouteOverviewPragmatic';
import SortedToPlanListPragmatic from './SortedToPlanListPragmatic/SortedToPlanListPragmatic';
import ToPlanDropzonePragmatic from './ToPlanDropzonePragmatic/ToPlanDropzonePragmatic';

import { DROP_LOADING_OVERLAY_Z_INDEX } from '../frozenDrop.constants';
import { useFrozenDrop } from '../hooks/useFrozenDrop';
import { usePlanAppointmentsWithFrozenDrop } from '../hooks/usePlanAppointmentsWithFrozenDrop';
import { routeConfirmation } from './CardsViewPragmatic.utils';
import { useColumnLoading } from './hooks/useColumnLoading';

import type { PlanAppointmentsRequest } from 'generated';
import type { ConnectAppointmentGroupingPayload } from 'RestAPIs/activities';
import type { EditAppointmentType } from 'RestAPIs/activities/types';
import type { FrozenDropHandle } from '../hooks/useFrozenDrop';
import type { UsePlanningReturn } from '../hooks/usePlanning';
import type { Event } from '../types';
import type { APNotification } from '../utils';
import type { PendingConfirmation } from './CardsViewPragmatic.utils';

// Suppress the browser's default drag cursors (the green "+" copy badge, the
// forbidden circle, etc.) and force a consistent "move" cursor across the
// whole window for the entire duration of any HTML5 drag.
//
// Registered at module load (NOT inside a useEffect) so the listeners exist
// before the user can possibly start a drag, and registered in the capture
// phase so we run BEFORE Pragmatic's own document-level listeners. Without
// this, the browser briefly shows its default initial cursor for the first
// frame of the drag, since `effectAllowed` is otherwise unset.
// Use a property on window as a one-time guard so that Vite HMR module
// re-evaluation and test environments that re-import this module do not
// register duplicate listeners.
declare global {
  interface Window {
    __cardsViewPragmaticDragListenersInstalled?: boolean;
  }
}
if (
  typeof document !== 'undefined' &&
  !window.__cardsViewPragmaticDragListenersInstalled
) {
  window.__cardsViewPragmaticDragListenersInstalled = true;
  document.addEventListener(
    'dragstart',
    (event: DragEvent) => {
      if (event.dataTransfer) {
        event.dataTransfer.effectAllowed = 'move';
      }
    },
    { capture: true }
  );
  // Accept every drag as a "move" everywhere — including outside any drop
  // target — so the browser never falls back to the forbidden cursor.
  const acceptAsMove = (event: DragEvent) => {
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move';
    }
    event.preventDefault();
  };
  document.addEventListener('dragenter', acceptAsMove, { capture: true });
  document.addEventListener('dragover', acceptAsMove, { capture: true });
}

export default function CardsViewPragmatic({
  startDate,
  endDate,
  currentPlanningEntityIdentifiers,
  planningEntities,
  setPlanningEntities,
  planAppointmentsAsync,
  connectAppointmentGrouping,
  boardKey,
  externalRefreshCount,
  isLoading
}: UsePlanningReturn) {
  'use memo';
  const [taskListIsOpen, setTaskListIsOpen] = useState<boolean>(true);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const [shouldShowPopoverDialog, setShouldShowPopoverDialog] = useRecoilState(
    shouldShowPopoverDialogAtom
  );

  const [activeEvent, setActiveEvent] = useState<Event | null>(null);

  // A single subscription gives both the setter and the value used below to
  // decide whether the warning dialog is holding the frozen card.
  const [warningDialogState, setDialogState] = useRecoilState(
    dialogManagerActiveAtom
  );
  // The action a confirmation popover is waiting on: the payload to send once
  // the user picks an occurrence option, plus the columns of the drag that
  // produced it. One slot holding both, written only where a popover is opened,
  // so a later drop can't hand this popover a different drop's payload or
  // columns — the popover itself is a single slot, so the action it stands for
  // has to be too. Stamped with the id it opens the popover under, so a stale
  // action from a popover that was never answered isn't picked up by the next —
  // see `routeConfirmation`.
  //
  // A ref, not state: pure bookkeeping for the drop in flight, read only from
  // callbacks and never rendered. As state it re-rendered this component — the
  // board root — from inside the drop handler, which is the frame that has to
  // show the drop feedback.
  const pendingConfirmationRef = useRef<PendingConfirmation | null>(null);

  const [activeEntityContainingSelection, setActiveEntityContainingSelection] =
    useState<string | null>(null);

  const [, setPlanResponsePlanningEntityIdentifiers] = useRecoilState(
    planResponsePlanningEntityIdentifiersAtom
  );

  const [pendingDropPosition, setPendingDropPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);

  const [isLoadingUnassignAppointment, setIsLoadingUnassignAppointment] =
    useState<boolean>(false);

  const { mutateAsync: unassignAppointmentAsync } =
    mutations.useRemoveAssignmentFromAppointments();

  // A recurring drop shows the "this / all occurrences" confirmation popover
  // before any API call fires. The action isn't finished until the user picks
  // an option, so keep the card frozen while that popover is open (alongside the
  // warning-dialog hold via `warningDialogState`).
  const isDragActionPopoverOpen =
    shouldShowPopoverDialog.id !== '' && !!shouldShowPopoverDialog.isDragAction;

  const isBusy = isLoading || isLoadingUnassignAppointment;

  // The action isn't finished while one of these is waiting on the user, even
  // though nothing is in flight.
  const holdWhileOpen =
    isDragActionPopoverOpen ||
    (warningDialogState.open && warningDialogState.type === DialogType.WARNING);

  // Per-operation loading state. Each in-flight drag mutation is tracked under
  // its own id, mapped to the columns (source + destination) it involves, so
  // only those columns spin and the rest of the board stays usable.
  const {
    columnIds: loadingColumnIds,
    boardOperationCount,
    beginDrag,
    start: startColumnLoading,
    startBoardWide: startBoardLoading,
    release: releaseColumnLoading
  } = useColumnLoading({
    isLoading: isBusy,
    holdWhileOpen,
    // Per-column overlays stand in for the global one, so they must only cover
    // loads that belong to *this* board. Change the date range or plan unit
    // while a drop is settling and the whole board reloads: that needs the
    // global overlay back, not a spinner on two columns of the old board.
    boardKey,
    // Lets a released operation tell its own result has landed, instead of
    // waiting for every other operation on the board to settle too.
    boardData: planningEntities
  });

  // A refresh the view never asked for reloads the whole board, so the
  // per-column overlays can't stand in for the global one — register it as its
  // own board-wide operation, exactly like a connect. Without this a websocket
  // refresh landing while a drop is still settling would replace every card on
  // the board behind two column spinners, on a board that still looks
  // interactive.
  //
  // Released straight away: `release` holds a released operation until its load
  // has settled, which is precisely the refetch's lifetime, and the watchdog
  // drops it if the refetch never materialises. Nothing else here has a terminal
  // callback to hang the release on.
  //
  // Guarded on a ref rather than a dependency array: `useColumnLoading` returns
  // fresh closures each render, so listing them would register an operation per
  // render, and leaving them out needs an eslint-disable — which opts the whole
  // component out of the React Compiler (see `'use memo'` above).
  const handledRefreshCountRef = useRef(0);
  useEffect(() => {
    if (externalRefreshCount === handledRefreshCountRef.current) return;
    handledRefreshCountRef.current = externalRefreshCount;
    releaseColumnLoading(startBoardLoading().id);
  });

  // Frozen drop preview: hold a copy of the card at the drop slot + faded
  // source until the call + refetch settle. See `useFrozenDrop`.
  const { freeze: freezeCardDrop } = useFrozenDrop(setFrozenCardDrop, {
    isLoading: isBusy,
    holdWhileOpen
  });

  // Shared drag-preview state, torn down once a mutation has failed. Only the
  // operation that still owns the preview may call this — see `ownsDragPreview`.
  // The frozen frame is *not* part of it: it carries its own ownership check and
  // has to be released even by an operation that lost the rest.
  const teardownDragPreview = () => {
    containerRef.current?.removeAttribute('data-drag-active');
    pendingConfirmationRef.current = null;
    setPendingDropPosition(null);
  };

  // Only resets drag state: the error snackbar is shown by the mutation hooks
  // (`useConnectAppointmentGrouping`) and by `usePlanAppointmentsWithFrozenDrop`.
  //
  // Used by the connect-appointment-grouping paths, which today never freeze a
  // drop — but release the newest freeze anyway rather than relying on that: the
  // handle no-ops when nothing is frozen, and without this an error on a path
  // that *does* freeze would leave a card copy floating over the board (and its
  // source faded) until the `useFrozenDrop` watchdog fires. Deliberately not
  // folded into `teardownDragPreview`: the plan-appointments error path below
  // releases through its own operation's handle, which correctly no-ops once a
  // newer drop owns the preview, and must not clear that newer preview instead.
  const onMutationError = () => {
    apDebug('mutationError', { source: 'connect/unassign path' });
    clearDropVerification('mutation error');
    getFrozenDrop()?.releaseNow();
    teardownDragPreview();
  };

  // Drop lifecycle shared with the Roster and Timeline views: which freeze and
  // which per-column operation a call is responsible for, and which callback
  // releases them when. This view only supplies its own side effects, each
  // guarded on still owning the shared drag-preview state — starting a newer
  // drag hands that over.
  const {
    registerFrozenDrop,
    getFrozenDrop,
    handlePlanAppointments: planAppointmentsWithFrozenDrop
  } = usePlanAppointmentsWithFrozenDrop({
    util: {
      // `setPayload` is deliberately dropped on the floor. The util reports the
      // payload it sent so a view can keep it for a deferred confirmation; this
      // one already has that in `pendingConfirmationRef`, written per popover.
      // Recording it here too is how an unrelated later drop used to overwrite
      // the payload the open popover was going to send.
      setPayload: () => {},
      setShouldShowPopoverDialog,
      setDialogState,
      planningEntitiesToEvents,
      getConflictingEvents,
      notificationsToHashTable,
      planningEntities,
      appendCorrelationToken,
      setPlanResponsePlanningEntityIdentifiers,
      setPlanningEntities,
      viewport: {
        startDate,
        endDate,
        planningEntityIdentifiers: currentPlanningEntityIdentifiers
      }
    },
    // Each operation needs its own promise. React Query's mutation observer
    // keeps only the latest call's callbacks, so with the plain `mutate` a
    // second drop would silently swallow this one's — leaving its columns
    // spinning with their drop targets unregistered.
    planAppointments: (planPayload, { onSuccess, onError }, call) =>
      void planAppointmentsAsync(planPayload)
        .then(onSuccess, onError)
        .catch((e: unknown) => {
          // Only reachable when `onSuccess` itself throws — it builds the
          // conflict dialog from the response. Nothing reported the operation
          // in that case, so release here rather than hold the columns
          // un-droppable until the watchdog.
          showError(e);
          call.releaseOperation();
          call.releaseFrozenNow();
        }),
    onError: ({ ownsDragPreview }) => {
      apDebug('planError', { ownsDragPreview: ownsDragPreview() });
      clearDropVerification('plan call failed');
      if (ownsDragPreview()) {
        teardownDragPreview();
      }
    },
    onCancel: ({ ownsDragPreview }) => {
      apDebug('planCancelled', { ownsDragPreview: ownsDragPreview() });
      clearDropVerification('warning dialog cancelled');
      if (ownsDragPreview()) {
        setPendingDropPosition(null);
        setActiveEvent(null);
      }
    },
    onSuccess: ({ ownsDragPreview }) => {
      if (ownsDragPreview()) {
        containerRef.current?.removeAttribute('data-drag-active');
        setSelectedIds([]);
        setActiveEntityContainingSelection(null);
        setPendingDropPosition(null);
        setActiveEvent(null);
      }
    }
  });

  const handlePlanAppointments = (
    payload: PlanAppointmentsRequest | null,
    columnIds: string[]
  ) =>
    planAppointmentsWithFrozenDrop(payload, () => {
      // Register this operation so only its columns spin, independently of any
      // other drag-and-drop that may already be in flight.
      const operation = startColumnLoading(columnIds);
      return {
        release: () => releaseColumnLoading(operation.id),
        ownsDragPreview: operation.ownsDragPreview
      };
    });

  // A connect reloads the board rather than named columns, so it registers a
  // board-wide operation: the full-screen overlay has to keep appearing for it
  // even while a drop is still settling with per-column overlays on screen.
  const sendConnect = (
    connectPayload: ConnectAppointmentGroupingPayload,
    onSuccess?: (response: any) => void
  ) => {
    const operation = startBoardLoading();
    const release = () => releaseColumnLoading(operation.id);

    connectAppointmentGrouping(connectPayload, {
      onSuccess: (response: any) => {
        release();
        onSuccess?.(response);
      },
      onError: () => {
        release();
        onMutationError();
      }
    });
  };

  const sendConnectAppointmentGrouping = (
    appointmentGroupingEntityId: string
  ) => {
    if (planningEntities) {
      const payload: ConnectAppointmentGroupingPayload = {
        appointmentGroupingId: appointmentGroupingEntityId,
        appointmentGroupingStart: startDate,
        editAppointmentType: 'ONLY_THIS',
        suppressWarnings: false
      };
      const connectAppointmentPayload = appendCorrelationToken(payload);

      const planningEntity = planningEntities[appointmentGroupingEntityId];
      const today = new Date();
      today.setUTCHours(0, 0, 0, 0);
      if (
        planningEntity.schedules
          .map((s) => s.events)
          .flat()
          .some((event) => event.isRecurring && !event.isAbsent) &&
        new Date(startDate) >= today
      ) {
        // Deferred to the confirmation popover; no columns, since a connect
        // reloads the board.
        pendingConfirmationRef.current = {
          popoverId: appointmentGroupingEntityId,
          payload: connectAppointmentPayload,
          columnIds: []
        };
        setShouldShowPopoverDialog({
          id: appointmentGroupingEntityId,
          scheduleStart: startDate
        });
      } else {
        sendConnect(connectAppointmentPayload, (response: any) => {
          if (!response.data.success) {
            const notifications = response.data
              .notifications as APNotification[];

            const events =
              planningEntities[
                connectAppointmentPayload.appointmentGroupingId
              ].schedules
                .map((s) => s.events)
                .flat() ?? [];
            const conflictingEvents: Event[] = getConflictingEvents(
              events || [],
              notifications
            );

            setDialogState({
              type: DialogType.WARNING,
              open: true,
              data: notificationsToHashTable(conflictingEvents, notifications),
              onSubmit: () => {
                sendConnect({
                  ...connectAppointmentPayload,
                  suppressWarnings: true
                } as ConnectAppointmentGroupingPayload);
              }
            });
          }
        });
      }
    }
  };

  /**
   * @param columnIds Columns to scope the loading state to — the source route
   * column of the drag that led here. Omitted when the unassign comes from the
   * context menu rather than a drop: nothing on screen identifies it as a column
   * action, so it registers as board-wide and keeps the full-screen overlay.
   * @param frozen The freeze this unassign is responsible for clearing. Passed
   * in rather than read through `getFrozenDrop`, which returns the newest drop
   * whether or not this call came from one — a context-menu unassign started
   * while a drop is settling would otherwise clear that drop's preview.
   */
  const unassignAppointment = (
    editAppointmentType: EditAppointmentType,
    columnIds?: string[],
    frozen?: FrozenDropHandle | null
  ) => {
    const operation = columnIds?.length
      ? startColumnLoading(columnIds)
      : startBoardLoading();

    // The util bails without firing — and so without ever calling `onFinally` —
    // when there is nothing to unassign. Its own loading flag marks the point it
    // commits to the request, so it also says whether a terminal callback is
    // still coming for this operation.
    let requestSent = false;

    void unassignAppointmentUtil({
      editAppointmentType,
      activeEvent,
      selectedIds,
      planningEntities,
      shouldShowPopoverDialog,
      appendCorrelationToken,
      unassignAppointmentAsync,
      setShouldShowPopoverDialog,
      setPlanResponsePlanningEntityIdentifiers,
      setActiveEntityContainingSelection,
      setIsLoading: (loading: boolean) => {
        if (loading) requestSent = true;
        setIsLoadingUnassignAppointment(loading);
      },
      onFinally: () => {
        containerRef.current?.removeAttribute('data-drag-active');
        // Deferred to the settle so the frozen frame covers the refetch too.
        // Absent when unassigning from the context menu instead of a drop.
        frozen?.releaseWhenSettled();
        releaseColumnLoading(operation.id);
        setIsLoadingUnassignAppointment(false);
        setActiveEvent(null);
        setActiveEntityContainingSelection(null);
        setPendingDropPosition(null);
        setSelectedIds([]);
      },
      onGetEvents: (planningEntity, isToPlan, selectedIds) => {
        return (
          (isToPlan
            ? planningEntity?.events.filter(({ id }: { id: string }) =>
                selectedIds.includes(id)
              )
            : planningEntity?.schedules
                .flatMap((s: any) => s.events)
                .filter(({ id }: { id: string }) =>
                  selectedIds.includes(id)
                )) || []
        );
      }
    })
      .then(() => {
        // Resolves as soon as the request is away, so this only catches the
        // bail: without it the operation would hold its columns until the
        // watchdog.
        if (!requestSent) releaseColumnLoading(operation.id);
      })
      .catch((e: unknown) => {
        // The util resolves before its request settles, so reaching here means
        // it threw on the way to firing the request. Its `onFinally` never ran,
        // so undo everything it would have: the loading flag it may already have
        // raised (it does that immediately before the call that can throw, and a
        // flag stuck true keeps the full-screen overlay up for good), the
        // operation's columns, and the frozen frame.
        setIsLoadingUnassignAppointment(false);
        releaseColumnLoading(operation.id);
        frozen?.releaseNow();
        showError(e);
      });
  };

  // --- Stable handler refs to avoid stale closures in the monitor, and to keep
  // the popover context value and the route-overview props referentially stable.
  const handleDragStartRef = useRef<(source: any) => void>(() => {});
  const handleDragEndRef = useRef<(source: any, location: any) => void>(
    () => {}
  );
  const handlePopoverCloseRef = useRef<() => void>(() => {});
  const handleAppointmentConfirmationRef = useRef<
    (editAppointmentType: EditAppointmentType, isUnassignment?: boolean) => void
  >(() => {});
  const sendConnectAppointmentGroupingRef = useRef<
    (appointmentGroupingEntityId: string) => void
  >(() => {});
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Identifies each frozen drop, so a repeat drop of the same card renders a
  // fresh preview instead of reusing the previous one's state.
  const dropIdRef = useRef(0);

  // Frame that takes `data-drag-active` back off after the drop has painted, so
  // it can be dropped if the view unmounts in between.
  const dragActiveCleanupFrameRef = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (dragActiveCleanupFrameRef.current !== null) {
        cancelAnimationFrame(dragActiveCleanupFrameRef.current);
      }
    },
    []
  );

  // No `useMemo`: the React Compiler already caches this on `[planningEntities]`
  // (verified in its output), and identity matters here — it is
  // `RouteOverviewPragmatic`'s heaviest prop.
  const planningEntitiesArray = planningEntities
    ? Object.values(planningEntities)
    : [];

  const handleDragStart = (source: any) => {
    const activePlanningEntityId = source.data?.planningEntityId;
    const activeId = source.data?.id;

    // Guard: ignore drags that don't carry our contract (e.g. external drags)
    if (!activePlanningEntityId || !activeId) return;

    apDebug('dragStart', {
      eventId: activeId,
      planningEntityId: activePlanningEntityId,
      isToPlan: source.data.isToPlan
    });

    // Start a new drag "generation" so a mutation still settling from an earlier
    // drag can't tear down this drag's preview state.
    beginDrag();

    containerRef.current?.setAttribute('data-drag-active', '');

    const isToPlan = source.data.isToPlan;

    const event = !isToPlan
      ? planningEntities?.[activePlanningEntityId]?.schedules
          .map((s) => s.events)
          .flat()
          .find(({ id }) => id === activeId) ?? null
      : planningEntities?.[activePlanningEntityId]?.events.find(
          ({ id }) => id === activeId
        ) ?? null;

    setActiveEvent(event);
    setActiveEntityContainingSelection(activePlanningEntityId);
    // Use `prev` from the functional updater to avoid a stale closure on `selectedIds`
    setSelectedIds((prev) => (prev.includes(activeId) ? prev : []));
  };

  const handleDragEnd = (source: any, location: any) => {
    const dropTargets: any[] = location.current.dropTargets;

    // No drop target — treat as cancel. Nothing to undo: a pragmatic drag
    // mutates nothing until the drop is sent.
    if (dropTargets.length === 0) {
      apDebug('dragEnd.cancelled', { reason: 'no drop target' });
      containerRef.current?.removeAttribute('data-drag-active');
      setActiveEvent(null);
      setPendingDropPosition(null);
      return;
    }

    const overData = dropTargets[0].data;
    const overElement = dropTargets[0].element as HTMLElement | undefined;
    const activeData = source.data;

    // Guard: ignore drags that don't carry our contract
    if (!activeData?.planningEntityId || !activeData?.id) {
      apDebug('dragEnd.ignored', { reason: 'drag without contract data' });
      return;
    }

    const activePlanningEntityId = activeData.planningEntityId;
    const activeIsToPlan = activeData.isToPlan;
    const overIsToPlan = overData.isToPlan;

    apDebug('dragEnd', {
      eventId: activeData.id,
      sourcePlanningEntityId: activePlanningEntityId,
      targetPlanningEntityId: overData.planningEntityId,
      activeIsToPlan,
      overIsToPlan,
      overStart: overData.start,
      overScheduleStart: overData.scheduleStart,
      selectedIds
    });

    if (activeIsToPlan === overIsToPlan) {
      apDebug('dragEnd.ignored', {
        reason: 'source and target are the same kind of container'
      });
      containerRef.current?.removeAttribute('data-drag-active');
      setPendingDropPosition(null);
      return;
    }

    if (activePlanningEntityId === undefined || !activeEvent) {
      apDebug('dragEnd.ignored', {
        reason: activeEvent
          ? 'no source planning entity'
          : 'no active event for this drag'
      });
      containerRef.current?.removeAttribute('data-drag-active');
      setActiveEvent(null);
      setPendingDropPosition(null);
      setSelectedIds([]);
      return;
    }

    const activeId = activeData.id;

    const ids = selectedIds.length ? selectedIds : [activeId];

    const selectedEvents: Event[] = ids
      .map((id) => findItem(planningEntities, activePlanningEntityId, id))
      .filter((e): e is Event => e != null);

    const occurrenceDate = activeEvent.start
      ? format(new Date(activeEvent.start), 'y-MM-dd')
      : startDate;

    const today = new Date();
    today.setUTCHours(0, 0, 0, 0);

    const dropPayload: PlanAppointmentsRequest = {
      appointmentOccurrences:
        selectedIds.length === 0
          ? [
              {
                entityId: activeEvent.id,
                originalOccurrenceStart: activeEvent.start,
                schedulingLabel: activeEvent.schedulingLabel
              }
            ]
          : sortByDate(
              selectedEvents.map((event: Event) => ({
                entityId: event.id,
                originalOccurrenceStart: event.start,
                schedulingLabel: event.schedulingLabel
              })),
              'originalOccurrenceStart'
            ),
      planningEntity: {
        ...overData.planningEntityIdentifier,
        start: overIsToPlan ? undefined : overData.scheduleStart
      },
      start: overData.start,
      editAppointmentType: 'ONLY_THIS',
      suppressWarnings: false,
      // The row being dragged out of. Completed with the board's dates and rows in handlePlanAppointments.
      viewport: sourceViewportContext(planningEntities, activePlanningEntityId)
    };

    // Measured here, before `data-drag-active` comes off below: that attribute
    // drives `[data-drag-active] &` rules across the board, so removing it
    // invalidates style for the whole subtree and any layout read after it has
    // to synchronously recalculate all of it. Reading first keeps the drop
    // handler off that path.
    const dropTargetRect = overElement?.getBoundingClientRect();

    // Freeze the last drag frame: hold a copy of the card at the drop slot +
    // faded source until the call + refetch settle. The final position is
    // determined by where the card is dropped (the route recomputes its start),
    // so anchor to the drop target's top-left and match its width.
    const freezeDrop = () => {
      const rect = dropTargetRect;
      const input = location.current.input;
      const position = rect
        ? { x: rect.left, y: rect.top }
        : { x: input.clientX, y: input.clientY };
      dropIdRef.current += 1;
      registerFrozenDrop(
        freezeCardDrop({
          dropId: dropIdRef.current,
          eventIds: ids,
          position,
          width: rect?.width ?? 0,
          count: ids.length,
          event: activeEvent,
          planningEntityId: activePlanningEntityId,
          planningEntityType: activeData.planningEntityType
        })
      );
    };

    // Scope the loading state to the columns this drag actually touches — the
    // whole point of the per-column overlays. The "to plan" list is not a route
    // column and shows no loading state, so it contributes no id on either side.
    //
    // NOTE on the `activeIsToPlan === overIsToPlan` guard above: cards always
    // carry a boolean `isToPlan` (`getInitialData` in DraggableCardPragmatic),
    // but route dropzones put no `isToPlan` in their drop-target data
    // (`useDropTarget`), so for a route→route drop the comparison is
    // `false === undefined` and the drop passes. In practice the guard only
    // rejects to-plan→to-plan; a route→route move reaches the plan branch and
    // contributes both route ids here.
    const columnLoadingIds = [
      ...(activeIsToPlan ? [] : [activePlanningEntityId]),
      ...(overIsToPlan ? [] : [overData.planningEntityId])
    ].filter(Boolean);

    // The drag itself is over — re-enable the rest of the board so the user can
    // grab a card from another column while this operation loads.
    // `data-drag-active` disables all cards' pointer events via CSS, so it must
    // not linger through the mutation. The busy columns stay protected by their
    // own per-column loading overlay + disabled drop targets. The frozen source
    // stays faded on its own via `useFrozenDrop`.
    //
    // Deferred until after the drop feedback has painted: the attribute drives
    // `[data-drag-active] &` rules across the whole board, so taking it off
    // invalidates style for every card — measured at ~280ms of style, layout and
    // paint on a full board, all of it in front of the frame that shows the
    // frozen card and the column spinners. It only re-enables pointer events, so
    // a frame later is imperceptible. Two frames: a single
    // `requestAnimationFrame` still runs before the paint it would be delaying.
    dragActiveCleanupFrameRef.current = requestAnimationFrame(() => {
      dragActiveCleanupFrameRef.current = requestAnimationFrame(() => {
        dragActiveCleanupFrameRef.current = null;
        // A drag started in the meantime owns the attribute now.
        if (isAnyDragActiveNow()) return;
        containerRef.current?.removeAttribute('data-drag-active');
      });
    });

    if (activeEvent?.isRecurring && new Date(occurrenceDate) >= today) {
      apDebug('dragEnd.routed', { branch: 'recurring popover' });
      // The confirmation may still resolve into a plan call, so record the drop
      // for verification now; closing the popover unrecords it.
      if (!overIsToPlan) {
        recordDropForVerification({
          eventIds: ids,
          sourcePlanningEntityId: activePlanningEntityId,
          targetPlanningEntityId: overData.planningEntityId
        });
      }
      // Recurring: freeze the card at the drop slot and keep it there while the
      // confirmation popover is open (held via `isDragActionPopoverOpen`), so it
      // doesn't vanish before the user picks an occurrence option.
      freezeDrop();
      // Use the drop position from Pragmatic's location to anchor the confirmation popover
      const input = location.current.input;
      pendingConfirmationRef.current = {
        popoverId: activeEvent.id,
        payload: dropPayload,
        columnIds: columnLoadingIds
      };
      setPendingDropPosition({ x: input.clientX, y: input.clientY });
      setShouldShowPopoverDialog({
        id: activeEvent.id,
        scheduleStart: overData.scheduleStart,
        isUnassignment: overIsToPlan,
        isDragAction: true
      });
    } else if (overIsToPlan) {
      apDebug('dragEnd.routed', { branch: 'unassign' });
      freezeDrop();
      unassignAppointment('ONLY_THIS', columnLoadingIds, getFrozenDrop());
    } else {
      apDebug('dragEnd.routed', { branch: 'plan' });
      recordDropForVerification({
        eventIds: ids,
        sourcePlanningEntityId: activePlanningEntityId,
        targetPlanningEntityId: overData.planningEntityId
      });
      freezeDrop();
      handlePlanAppointments(dropPayload, columnLoadingIds);
    }
  };

  const handlePopoverClose = () => {
    // Only a drag-action popover can have a freeze behind it — the connect paths
    // never freeze. Every popover in this view shares this callback (the sticky
    // header's route-unassign and route-connect ones included), so without the
    // gate closing one of those would clear the preview of a drop that is still
    // in flight.
    if (shouldShowPopoverDialog.isDragAction) {
      apDebug('popoverClosed', { popoverId: shouldShowPopoverDialog.id });
      clearDropVerification('confirmation popover closed');
      getFrozenDrop()?.releaseNow();
    }
    setActiveEvent(null);
    setPendingDropPosition(null);
  };

  // Declared above the latching effect below, not after it: the React Compiler
  // rejects a forward reference from the effect to a handler declared later
  // ("Cannot access variable before it is declared") and then silently bails out
  // of optimizing this whole component — no compiler memoization at all. Keep
  // every latched handler above that effect.
  const handleAppointmentConfirmation = (
    editAppointmentType: EditAppointmentType,
    isUnassignment?: boolean
  ) => {
    // What this popover resolves to, decided from the action registered for it
    // rather than the newest drop's — which is what keeps a second drop from
    // redirecting an open confirmation. Pure and unit-tested; see
    // `routeConfirmation`.
    const action = routeConfirmation({
      pending: pendingConfirmationRef.current,
      openPopoverId: shouldShowPopoverDialog.id,
      isUnassignment,
      isDragAction: shouldShowPopoverDialog.isDragAction
    });

    if (action.kind === 'unassign') {
      return unassignAppointment(
        editAppointmentType,
        action.columnIds,
        action.releasesFrozenDrop ? getFrozenDrop() : null
      );
    }

    // Nothing belongs to this popover, so nothing is sent. Falling through to the
    // connect branch below would post a payload carrying nothing but a
    // correlation token — a connect with no `appointmentGroupingId`, which the
    // server can only reject, behind the board-wide overlay `sendConnect` raises
    // and holding it until it does.
    if (action.kind === 'none') {
      // Both, like every other terminal path here. `handleDragStart` sets
      // `activeEvent` without touching the position, and the drag portal renders
      // on `activeEvent && pendingDropPosition` — so a position left behind would
      // paint this drop's preview again the moment the next drag starts.
      setActiveEvent(null);
      setPendingDropPosition(null);
      // No `getFrozenDrop()?.releaseNow()`, unlike the unassign branch above: the
      // paths that reach `none` are the ones where the live freeze belongs to a
      // *newer* drop, so releasing it here would tear down a preview that is
      // still in flight. Same reason `handlePopoverClose` gates on `isDragAction`.
      return;
    }

    if (action.kind === 'plan') {
      handlePlanAppointments(
        { ...action.payload, editAppointmentType },
        action.columnIds
      );
      return;
    }

    const connectAppointmentPayload = appendCorrelationToken(action.payload);
    sendConnect(
      {
        ...connectAppointmentPayload,
        editAppointmentType
      } as ConnectAppointmentGroupingPayload,
      (response: any) => {
        if (
          !response.data.success &&
          connectAppointmentPayload &&
          'appointmentGroupingId' in connectAppointmentPayload &&
          planningEntities
        ) {
          const events =
            planningEntities[
              connectAppointmentPayload.appointmentGroupingId
            ].schedules
              .map((s) => s.events)
              .flat() ?? [];
          const notifications = response.data.notifications as APNotification[];
          const conflictingEvents: Event[] = getConflictingEvents(
            events || [],
            notifications
          );

          setDialogState({
            type: DialogType.WARNING,
            open: true,
            data: notificationsToHashTable(conflictingEvents, notifications),
            onSubmit: () => {
              sendConnect({
                ...connectAppointmentPayload,
                editAppointmentType,
                suppressWarnings: true
              } as ConnectAppointmentGroupingPayload);
            },
            onCancel: () => {
              setActiveEvent(null);
            }
          });
        } else {
          setActiveEvent(null);
        }
      }
    );
  };

  // Keep refs pointing at the latest closures. Assigned in a layout effect
  // (never during render) so the React Compiler can optimize this component;
  // useLayoutEffect runs synchronously after commit, so the monitor never
  // dispatches into a stale `.current` right after a render.
  useLayoutEffect(() => {
    handleDragStartRef.current = handleDragStart;
    handleDragEndRef.current = handleDragEnd;
    handlePopoverCloseRef.current = handlePopoverClose;
    handleAppointmentConfirmationRef.current = handleAppointmentConfirmation;
    sendConnectAppointmentGroupingRef.current = sendConnectAppointmentGrouping;
  });

  // Trampolines over the refs latched above, not the handlers themselves, and
  // deliberately without `useCallback`. Why the indirection is what earns the
  // stability, and why that stability is load-bearing: see the latch contract on
  // `PopOverConfirmationContext`.
  const onClose = () => handlePopoverCloseRef.current?.();
  const onHandleAppointmentConfirmation = (
    editAppointmentType: EditAppointmentType,
    isUnassignment?: boolean
  ) =>
    handleAppointmentConfirmationRef.current?.(
      editAppointmentType,
      isUnassignment
    );
  const connectAppointmentGroupingStable = (
    appointmentGroupingEntityId: string
  ) => sendConnectAppointmentGroupingRef.current?.(appointmentGroupingEntityId);

  // No `useMemo`: with the trampolines above permanently stable, the compiler
  // leaves `isLoading` as this object's only dependency. What that costs and
  // where it goes next: see the latch contract on `PopOverConfirmationContext`.
  const popOverConfirmationValue = {
    isLoading,
    onClose,
    onHandleAppointmentConfirmation
  };

  // Register the Pragmatic monitor once; use refs to call current handlers
  useEffect(() => {
    return monitorForElements({
      onDragStart: ({ source }) => handleDragStartRef.current(source),
      onDrop: ({ source, location }) =>
        handleDragEndRef.current(source, location)
    });
  }, []);

  return (
    <PopOverConfirmationContext.Provider value={popOverConfirmationValue}>
      {/* The per-column overlays stand in for this one, so it steps aside while
          columns are loading — but only for loads that *are* column-scoped. An
          initial load, a board-wide action (connect, a context-menu unassign),
          or a websocket refresh still blocks the board, even when it starts
          while an earlier drop is settling. The last two get there by
          registering a board-wide operation; an initial load or a board change
          has no column-scoped operation to be mistaken for in the first
          place. */}
      <LoadingOverlay
        loading={
          isBusy && (boardOperationCount > 0 || loadingColumnIds.size === 0)
        }
        zIndex={DROP_LOADING_OVERLAY_Z_INDEX}
      />
      <Box
        ref={containerRef}
        display="flex"
        position="relative"
        overflow="hidden"
        height="100%"
      >
        <Box
          sx={{
            width: '100%',
            height: '100%',
            overflowX: 'auto',
            minWidth: taskListIsOpen ? 'initial' : '100%',
            maxWidth: taskListIsOpen
              ? `calc(100% - ${TASK_LIST_WIDTH}px)`
              : 'initial',
            position: 'relative',
            top: 0,
            borderLeft: (theme) => `1px solid ${theme.palette.divider}`
          }}
        >
          <RouteOverviewPragmatic
            activeEntityContainingSelection={activeEntityContainingSelection}
            onChangeSelection={setSelectedIds}
            onChangeActiveEntity={setActiveEntityContainingSelection}
            planningEntities={planningEntitiesArray}
            connectAppointmentGrouping={connectAppointmentGroupingStable}
            loadingEntityIds={loadingColumnIds}
          />
        </Box>

        <TaskList
          onOpen={setTaskListIsOpen.bind(undefined, true)}
          onClose={setTaskListIsOpen.bind(undefined, false)}
          planningEntities={planningEntities}
          dropzonePadding="10px"
          activeEntityContainingSelection={activeEntityContainingSelection}
          onChangeActiveEntity={setActiveEntityContainingSelection}
          onChangeSelection={setSelectedIds}
          DropzoneComponent={ToPlanDropzonePragmatic}
          SortedToPlanListComponent={SortedToPlanListPragmatic}
        />
      </Box>
      {activeEvent && pendingDropPosition && (
        <DragPortalPragmatic
          activeEvent={activeEvent}
          selectedIds={selectedIds}
          pendingDropPosition={pendingDropPosition}
        />
      )}
      <FrozenDropCardPragmatic />
    </PopOverConfirmationContext.Provider>
  );
}
